import { Injectable, inject } from '@angular/core';
import { Action, Selector, State } from '@ngxs/store';
import type { StateContext } from '@ngxs/store';
import { finalize, from, Observable, of, switchMap, tap } from 'rxjs';
import type { CountrySearchResult, LocationSearchResult } from '@sal/location/application/ports';
import type { ISearchParams } from '../../interfaces/ISearchParams';
import { SearchService } from '../../services/search.service';
import { ClearSearchResults, LoadSearchResults } from './search-results.actions';

export interface SearchResultsStateModel {
    isLoading: boolean;
    countries: CountrySearchResult[];
    locations: LocationSearchResult[];
    loadedLanguage: string | null;
}

@State<SearchResultsStateModel>({
    name: 'searchResultsState',
    defaults: {
        isLoading: false,
        countries: [],
        locations: [],
        loadedLanguage: null
    }
})
@Injectable()
export class SearchResultsState {
    private readonly searchService = inject(SearchService);

    @Selector()
    static isLoading(state: SearchResultsStateModel): boolean {
        return state.isLoading;
    }

    @Selector()
    static countries(state: SearchResultsStateModel): CountrySearchResult[] {
        return state.countries;
    }

    @Selector()
    static locations(state: SearchResultsStateModel): LocationSearchResult[] {
        return state.locations;
    }

    @Action(ClearSearchResults)
    public clearSearchResults(ctx: unknown): void {
        const stateContext = ctx as StateContext<SearchResultsStateModel>;
        stateContext.patchState({
            countries: [],
            locations: [],
            isLoading: false
        });
    }

    @Action(LoadSearchResults)
    public loadSearchResults(
        ctx: unknown,
        action: { payload: unknown }
    ): Observable<{ countries: CountrySearchResult[]; locations: LocationSearchResult[] }> {
        const stateContext = ctx as StateContext<SearchResultsStateModel>;
        const payload = action.payload as { searchParams: ISearchParams; language: string };

        stateContext.patchState({ isLoading: true });

        return from(this.searchService.searchLocations(payload.searchParams)).pipe(
            switchMap(locations => {
                const countrySearchTerm = this.getCountrySearchTerm(payload.searchParams);

                if (!countrySearchTerm) {
                    return of({ countries: [], locations });
                }

                return from(this.searchService.searchCountries(countrySearchTerm)).pipe(
                    switchMap(countries => of({ countries, locations }))
                );
            }),
            tap(({ countries, locations }) => {
                stateContext.patchState({
                    countries,
                    locations,
                    loadedLanguage: payload.language
                });
            }),
            finalize(() => {
                stateContext.patchState({ isLoading: false });
            })
        );
    }

    private getCountrySearchTerm(searchParams: ISearchParams): string | null {
        if (!searchParams.isAdvancedSearch) {
            return searchParams.searchString || null;
        }

        return searchParams.advancedSearchParams?.country || null;
    }
}
